<template>
   <div class="google-reviews-wrapper">
       <div class='sk-ww-google-reviews' :data-embed-id='embedId' :class="{ 'show-widget': displayStyle === 'masonry' && hasReviews }"></div>

       <div v-if="!hasReviews" class="loading-message">
           Loading reviews...
       </div>

       <!-- Carousel Layout -->
       <transition name="fade" mode="out-in">
           <div class="reviews-carousel" v-if="showCarousel" key="carousel">
               <!-- Left Arrow -->
               <button
                   v-if="shouldShowArrows"
                   @click="prevSlide"
                   :disabled="currentIndex === 0"
                   :class="['carousel-arrow', 'carousel-arrow-left', { 'inactive': currentIndex === 0 }]"
                   aria-label="Previous reviews">
                   <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                       <polyline points="15 18 9 12 15 6"></polyline>
                   </svg>
               </button>

               <!-- Carousel Track -->
               <div class="carousel-track-container"
                   @touchstart="handleTouchStart"
                   @touchmove="handleTouchMove"
                   @touchend="handleTouchEnd">
                   <div class="carousel-track" :style="{ transform: carouselTransform }">
                       <!-- Google Badge (First Item) -->
                       <div
                           v-if="hasBadge"
                           key="badge"
                           class="carousel-item carousel-badge-item"
                           v-html="badgeHtml">
                       </div>
                       <!-- Review Items -->
                       <div
                           v-for="(review, index) in filteredReviews"
                           :key="`review-${index}`"
                           class="carousel-item"
                           v-html="review.html">
                       </div>
                   </div>
               </div>

               <!-- Right Arrow -->
               <button
                   v-if="shouldShowArrows"
                   @click="nextSlide"
                   class="carousel-arrow carousel-arrow-right"
                   aria-label="Next reviews">
                   <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                       <polyline points="9 18 15 12 9 6"></polyline>
                   </svg>
               </button>
           </div>
       </transition>
   </div>
</template>


<script>
export default {
   name: 'GoogleReviews',
   props: {
       embedId: {
           type: String,
           required: true
       },
       minRating: {
           type: Number,
           default: 1
       },
       displayStyle: {
           type: String,
           default: 'carousel',
           validator: (value) => ['carousel', 'masonry'].includes(value)
       },
       maxReviewsToShow: {
           type: Number,
           default: 0
       }
   },
   data() {
       return {
           filteredReviews: [],
           badgeHtml: null,
           currentIndex: 0,
           itemsPerSlide: 3,
           brandingObserver: null,
           reviewsObserver: null,
           reviewsInterval: null,
           touchStartX: 0,
           touchEndX: 0,
           touchStartY: 0,
           touchEndY: 0,
           isSwiping: false,
           windowWidth: window.innerWidth
       };
   },
   computed: {
       maxIndex() {
           // Include badge in carousel count if it exists
           const totalItems = this.filteredReviews.length + (this.badgeHtml ? 1 : 0);
           return Math.max(0, totalItems - this.itemsPerSlide);
       },
       hasReviews() {
           return this.filteredReviews.length > 0;
       },
       hasBadge() {
           return this.badgeHtml !== null;
       },
       showCarousel() {
           return this.hasReviews && this.displayStyle === 'carousel';
       },
       showMasonry() {
           return this.hasReviews && this.displayStyle === 'masonry';
       },
       carouselTransform() {
           return `translateX(-${this.currentIndex * (100 / this.itemsPerSlide)}%)`;
       },
       shouldShowArrows() {
           // Count only reviews (not badge)
           const reviewCount = this.filteredReviews.length;

           // >1200px: hide arrows if less than 3 reviews
           if (this.windowWidth > 1200) {
               return reviewCount >= 3;
           }

           // 768px-1200px: hide arrows if less than 2 reviews
           if (this.windowWidth >= 768 && this.windowWidth <= 1200) {
               return reviewCount >= 2;
           }

           // <768px: always show arrows (for swipe navigation)
           return true;
       }
   },
   mounted() {
       // Load the SociableKIT widget script
       this.loadWidget();

       // Watch for the branding element to appear and hide it
       this.watchForBranding();

       // Filter reviews and extract them for carousel
       this.filterReviewsByRating();

       // Set initial items per slide based on screen width
       this.updateItemsPerSlide();

       // Update items per slide on window resize with throttling
       this.throttledResize = this.throttle(this.updateItemsPerSlide, 200);
       window.addEventListener('resize', this.throttledResize);
   },
   beforeUnmount() {
       // Clean up event listeners
       if (this.throttledResize) {
           window.removeEventListener('resize', this.throttledResize);
       }

       // Clean up observers
       if (this.brandingObserver) {
           this.brandingObserver.disconnect();
       }
       if (this.reviewsObserver) {
           this.reviewsObserver.disconnect();
       }
       if (this.reviewsInterval) {
           clearInterval(this.reviewsInterval);
       }
   },
   methods: {
       throttle(func, wait) {
           let timeout;
           return function executedFunction(...args) {
               const later = () => {
                   clearTimeout(timeout);
                   func(...args);
               };
               clearTimeout(timeout);
               timeout = setTimeout(later, wait);
           };
       },
       loadWidget() {
           // Check if script is already loaded
           if (document.querySelector('script[src*="sociablekit.com/google-reviews/widget.js"]')) {
               return;
           }

           // Load the CSS for SociableKIT styling
           if (!document.querySelector('link[href*="sociablekit.com"]')) {
               const link = document.createElement('link');
               link.rel = 'stylesheet';
               link.href = 'https://widgets.sociablekit.com/google-reviews/widget.css';
               document.head.appendChild(link);
           }

           // Create and append the script
           const script = document.createElement('script');
           script.src = 'https://widgets.sociablekit.com/google-reviews/widget.js';
           script.defer = true;
           document.body.appendChild(script);
       },
       watchForBranding() {
           // Use MutationObserver to watch for the branding element
           this.brandingObserver = new MutationObserver(() => {
               const branding = document.querySelector('.sk_branding');
               if (branding) {
                   branding.style.display = 'none';
                   if (this.brandingObserver) {
                       this.brandingObserver.disconnect();
                       this.brandingObserver = null;
                   }
               }
           });

           const wrapper = this.$el;
           if (wrapper) {
               this.brandingObserver.observe(wrapper, {
                   childList: true,
                   subtree: true
               });

               setTimeout(() => {
                   const branding = document.querySelector('.sk_branding');
                   if (branding) {
                       branding.style.display = 'none';
                       if (this.brandingObserver) {
                           this.brandingObserver.disconnect();
                           this.brandingObserver = null;
                       }
                   }
               }, 1000);
           }
       },
       filterReviewsByRating() {
           let hasProcessedInitialLoad = false;

           const processReviews = () => {
               const hasReviews = this.extractReviews();

               if (hasReviews && !hasProcessedInitialLoad) {
                   hasProcessedInitialLoad = true;
               }

               return hasReviews;
           };

           // Watch for DOM changes
           this.reviewsObserver = new MutationObserver(() => {
               if (!hasProcessedInitialLoad) {
                   processReviews();
               }
           });

           const wrapper = this.$el;
           if (wrapper) {
               this.reviewsObserver.observe(wrapper, {
                   childList: true,
                   subtree: true
               });

               // Initial polling until reviews load
               this.reviewsInterval = setInterval(() => {
                   if (processReviews()) {
                       clearInterval(this.reviewsInterval);
                       this.reviewsInterval = null;
                       if (this.reviewsObserver) {
                           this.reviewsObserver.disconnect();
                           this.reviewsObserver = null;
                       }
                   }
               }, 500);

               // Stop polling after 10 seconds
               setTimeout(() => {
                   if (this.reviewsInterval) {
                       clearInterval(this.reviewsInterval);
                       this.reviewsInterval = null;
                   }
                   if (this.reviewsObserver) {
                       this.reviewsObserver.disconnect();
                       this.reviewsObserver = null;
                   }
               }, 10000);
           }
       },
       extractReviews() {
           const widgetContainer = this.$el.querySelector('.sk-ww-google-reviews');
           if (!widgetContainer) return false;

           // Look for both masonry items (old) and slide items (new carousel format)
           const reviewItems = widgetContainer.querySelectorAll('.sk-masonry-item, .sk-slide-item');

           if (reviewItems.length === 0) return false;

           const reviews = [];

           reviewItems.forEach((item) => {
               // Find the post element (could be direct child or nested)
               const postElement = item.querySelector('.sk-post, .sk-badge');
               if (!postElement) return;

               // Check if this is the badge element
               if (postElement.classList.contains('sk-badge')) {
                   // Capture the badge if we haven't already
                   if (!this.badgeHtml) {
                       const clonedBadge = postElement.cloneNode(true);
                       this.badgeHtml = clonedBadge.outerHTML;
                   }
                   return;
               }

               const ratingElement = postElement.querySelector('.sk-post__rating');
               if (!ratingElement) return;

               // Count only filled/active stars (SociableKIT shows all 5 stars, some filled, some empty)
               const allStars = ratingElement.querySelectorAll('.sk-post__rating-icon');
               let rating = 0;

               // Method 1: Check for filled stars by looking at opacity or visibility
               allStars.forEach((star) => {
                   const computedStyle = window.getComputedStyle(star);
                   const opacity = computedStyle.opacity || star.style.opacity;
                   const display = computedStyle.display;
                   const visibility = computedStyle.visibility;

                   // Check path fill attribute
                   const path = star.querySelector('path');
                   const pathFill = path ? path.getAttribute('fill') : null;
                   const svgFill = star.getAttribute('fill');

                   // Star is filled if:
                   // - opacity is not 0
                   // - display is not none
                   // - visibility is not hidden
                   // - has a fill color (not 'none')
                   const isFilled = (
                       opacity !== '0' &&
                       display !== 'none' &&
                       visibility !== 'hidden' &&
                       (pathFill !== 'none' && pathFill !== null) ||
                       (svgFill !== 'none' && svgFill !== null)
                   );

                   if (isFilled) {
                       rating++;
                   }
               });

               // Fallback: if detection failed, count visible stars
               if (rating === 0 && allStars.length > 0) {
                   allStars.forEach((star) => {
                       const computedStyle = window.getComputedStyle(star);
                       if (computedStyle.opacity !== '0' && computedStyle.display !== 'none') {
                           rating++;
                       }
                   });
               }

               if (rating >= this.minRating) {
                   const clonedItem = postElement.cloneNode(true);

                   reviews.push({
                       html: clonedItem.outerHTML,
                       rating: rating
                   });
               }
           });

           // Limit to maxReviewsToShow if set (0 = show all)
           if (this.maxReviewsToShow > 0 && reviews.length > this.maxReviewsToShow) {
               reviews.splice(this.maxReviewsToShow);
           }

           this.filteredReviews = reviews;

           return reviews.length > 0;
       },
       nextSlide() {
           if (this.currentIndex < this.maxIndex) {
               this.currentIndex += this.itemsPerSlide;
               // Don't go past the max
               if (this.currentIndex > this.maxIndex) {
                   this.currentIndex = this.maxIndex;
               }
           } else {
               // Return to start
               this.currentIndex = 0;
           }
       },
       prevSlide() {
           if (this.currentIndex > 0) {
               this.currentIndex -= this.itemsPerSlide;
               // Don't go below 0
               if (this.currentIndex < 0) {
                   this.currentIndex = 0;
               }
           }
       },
       updateItemsPerSlide() {
           const width = window.innerWidth;
           this.windowWidth = width;
           if (width <= 768) {
               this.itemsPerSlide = 1;
           } else if (width <= 1199) {
               this.itemsPerSlide = 2;
           } else {
               this.itemsPerSlide = 3;
           }
           // Reset to start if current index is now out of bounds
           if (this.currentIndex > this.maxIndex) {
               this.currentIndex = 0;
           }
       },
       handleTouchStart(event) {
           this.touchStartX = event.touches[0].clientX;
           this.touchStartY = event.touches[0].clientY;
           this.isSwiping = true;
       },
       handleTouchMove(event) {
           if (!this.isSwiping) return;

           // Get current touch position
           const currentX = event.touches[0].clientX;
           const currentY = event.touches[0].clientY;

           // Calculate movement
           const diffX = Math.abs(currentX - this.touchStartX);
           const diffY = Math.abs(currentY - this.touchStartY);

           // If horizontal swipe is dominant, prevent default scrolling
           if (diffX > diffY && diffX > 10) {
               event.preventDefault();
           }
       },
       handleTouchEnd(event) {
           if (!this.isSwiping) return;

           this.touchEndX = event.changedTouches[0].clientX;
           this.touchEndY = event.changedTouches[0].clientY;
           this.isSwiping = false;

           // Calculate swipe distance
           const diffX = this.touchStartX - this.touchEndX;
           const diffY = Math.abs(this.touchStartY - this.touchEndY);

           // Minimum swipe distance (50px) and ensure horizontal swipe
           const minSwipeDistance = 50;

           // Only trigger if horizontal swipe is dominant
           if (Math.abs(diffX) > minSwipeDistance && Math.abs(diffX) > diffY) {
               if (diffX > 0) {
                   // Swiped left - go to next slide
                   this.nextSlide();
               } else {
                   // Swiped right - go to previous slide
                   this.prevSlide();
               }
           }
       }
   }
}
</script>


<style scoped>
.google-reviews-wrapper {
   position: relative;
   width: 100%;
   min-height: 300px;
}

.loading-message {
   text-align: center;
   padding: 40px 20px;
   color: #555555;
   font-size: 16px;
   animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
   0%, 100% {
       opacity: 1;
   }
   50% {
       opacity: 0.5;
   }
}

/* Fade transition for carousel and masonry */
.fade-enter-active, .fade-leave-active {
   transition: opacity 300ms ease-in-out;
}

.fade-enter-from, .fade-leave-to {
   opacity: 0;
}

.fade-enter-to, .fade-leave-from {
   opacity: 1;
}

/* Hide the original SociableKIT widget by default (for carousel mode) */
.sk-ww-google-reviews {
   display: none;
   visibility: hidden;
   position: absolute;
   width: 0;
   height: 0;
   overflow: hidden;
}

/* Show the widget for masonry mode - let SociableKIT handle the layout */
.sk-ww-google-reviews.show-widget {
   display: block !important;
   visibility: visible !important;
   position: static !important;
   width: 100% !important;
   height: auto !important;
   overflow: visible !important;
}

.reviews-carousel {
   display: flex;
   align-items: center;
   gap: 2rem;
   position: relative;
   width: 100%;
   --column-count: 3;
}

.carousel-arrow {
   flex-shrink: 0;
   width: 56px;
   height: 56px;
   border-radius: 50%;
   background-color: #2d2d2d;
   border: none;
   display: flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;
   transition: all 0.3s ease;
   color: white;
   z-index: 10;
}

.carousel-arrow:hover:not(:disabled) {
   background-color: #1a1a1a;
   transform: scale(1.05);
}

.carousel-arrow:disabled,
.carousel-arrow.inactive {
   opacity: 0.3;
   cursor: not-allowed;
}

.carousel-arrow svg {
   width: 28px;
   height: 28px;
}

.carousel-track-container {
   flex: 1;
   overflow: hidden;
   position: relative;
   min-width: 0;
   touch-action: pan-y pinch-zoom;
   cursor: grab;
}

.carousel-track-container:active {
   cursor: grabbing;
}

.carousel-track {
   display: flex;
   flex-direction: row;
   flex-wrap: nowrap;
   transition: transform 1s ease-in-out;
   align-items: center;
   width: 100%;
   padding-top: 1em;
   padding-bottom: 1em;
   height: 300px;
   justify-content: flex-start;
   flex: 0 0 100%;
}

.carousel-item {
   flex: 0 0 calc(100% / var(--column-count));
   height: 300px;
   padding: 15px;
   box-sizing: border-box;
}

/* Reset positioning and ensure card display for cloned post elements */
.carousel-item :deep(.sk-post) {
   background-color: #ffffff;
   color: #555555;
   box-shadow: 0px 4px 14px rgba(67, 89, 124, 0.20);
   border-radius: 10px;
   margin: 0;
   border: none;
   height: 100%;
   display: flex;
   flex-direction: column;
}

/* Override any SociableKIT inline styles */
.carousel-item :deep(.sk-post[style]) {
   position: static;
   left: auto;
   top: auto;
}

/* Remove transforms from all children */
.carousel-item :deep(*) {
   transform: none;
}

/* Fix the header layout - image next to details */
.carousel-item :deep(.sk-post__header) {
   display: flex;
   flex-direction: row;
   align-items: flex-start;
   gap: 12px;
   position: relative;
   width: 100%;
   left: auto;
   top: auto;
   padding: 20px 20px 16px;
   border-bottom: none;
   flex-shrink: 0;
}

.carousel-item :deep(.sk-post__user-image-container) {
   flex-shrink: 0;
   position: relative;
   width: 60px;
   height: 60px;
   border-radius: 50%;
   overflow: hidden;
}

.carousel-item :deep(.sk-post__user-image) {
   width: 60px;
   height: 60px;
   border-radius: 50%;
   object-fit: cover;
}

.carousel-item :deep(.sk-post__head-details) {
   flex: 1;
   display: flex;
   flex-direction: column;
   position: relative;
}

.carousel-item :deep(.sk-post__rating) {
   display: flex;
   flex-direction: row;
   gap: 4px;
}

.carousel-item :deep(.sk-post__rating-icon) {
   fill: #fbbc04;
   color: #fbbc04;
   width: 16px;
   height: 16px;
}

.carousel-item :deep(.sk-post__rating-icon path) {
   fill: #fbbc04;
}

/* Ensure the review body and footer are full width */
.carousel-item :deep(.sk-post__body) {
   position: relative;
   width: auto;
   left: auto;
   top: auto;
   line-height: 1.5em;
   margin: 0 20px;
   padding-bottom: 12px;
   scrollbar-gutter: stable;
   transition: all 0.2s ease-out;
   box-sizing: border-box;
   flex: 1 1 auto;
   min-height: 0;
   overflow: hidden;
}

.carousel-item :deep(.sk-post__text) {
   display: -webkit-box;
   -webkit-box-orient: vertical;
   -webkit-line-clamp: 4;
   line-clamp: 4;
   overflow: hidden;
   text-overflow: ellipsis;
}

.carousel-item :deep(.sk-post__text-content) {
   max-width: 100%;
   word-wrap: break-word;
}

.carousel-item :deep(.sk-post__text-content) > div {
   display: inline;
}

.carousel-item :deep(.sk-post__owners) {
   display: none;
}

.carousel-item :deep(.sk-post__footer) {
   position: relative;
   width: 100%;
   left: auto;
   top: auto;
   padding: 0 20px 20px;
   flex-shrink: 0;
   margin-top: auto;
}

.carousel-item :deep(.sk-post__button) {
   display: flex;
   flex-direction: row;
   align-items: center;
   gap: 8px;
}

.carousel-item :deep(.sk-post__button p) {
   margin-bottom: 0;
}

/* Badge Styling for Carousel */
.carousel-badge-item :deep(.sk-badge) {
   background-color: #ffffff;
   box-shadow: 0px 4px 14px rgba(67, 89, 124, 0.20);
   border-radius: 10px;
   border: none;
   height: 100%;
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   text-align: center;
   padding: 30px 20px;
}

.carousel-badge-item :deep(.sk-badge__logo) {
   display: flex;
   align-items: center;
   height: 36px;
   margin-bottom: 16px;
}

.carousel-badge-item :deep(.sk-badge__logo img) {
   width: auto;
   height: 100%;
}

.carousel-badge-item :deep(.sk-badge__name) {
   font-size: 18px;
   font-weight: 600;
   color: #202124;
   margin-bottom: 8px;
}

.carousel-badge-item :deep(.sk-badge__rating) {
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 1rem;
   font-size: 16px;
   margin-bottom: 8px;
}

.carousel-badge-item :deep(.sk-badge__value) {
   font-size: 2em;
   font-weight: bold;
   margin-bottom: 0;
}

.carousel-badge-item :deep(.sk-badge__rating-number) {
   font-size: 2em;
   font-weight: bold;
   color: #202124;
}

.carousel-badge-item :deep(.sk-badge__stars) {
   display: flex !important;
}

.carousel-badge-item :deep(.sk-badge__stars svg) {
   width: 2em;
   height: 2em;
}

.carousel-badge-item :deep(.sk-badge__stars path) {
   fill: #fbbc04;
}

.carousel-badge-item :deep(.sk-badge__stars .sk-star-empty path) {
   fill: rgba(0, 0, 0, 0.1) !important;
}

.carousel-badge-item :deep(.sk-badge__rating-stars) {
   display: flex;
   gap: 2px;
}

.carousel-badge-item :deep(.sk-badge__rating-icon) {
   fill: #fbbc04;
   color: #fbbc04;
   width: 20px;
   height: 20px;
}

.carousel-badge-item :deep(.sk-badge__review-count) {
   font-size: 14px;
   color: #777777;
   margin-bottom: 16px;
}

.carousel-badge-item :deep(.sk-badge__link) {
   color: #1a73e8;
   text-decoration: none;
   font-weight: 500;
   font-size: 14px;
}

.carousel-badge-item :deep(.sk-badge__link:hover) {
   text-decoration: underline;
}

/* Responsive adjustments */
@media (max-width: 1199px) {
   .reviews-carousel {
       --column-count: 2;
   }
}

@media (max-width: 768px) {
   .reviews-carousel {
       --column-count: 1;
       gap: 0;
       position: relative;
   }

   .carousel-arrow {
       width: 36px;
       height: 36px;
       position: absolute;
       z-index: 20;
   }

   .carousel-arrow-left {
       left: 5px;
   }

   .carousel-arrow-right {
       right: 5px;
   }

   .carousel-arrow svg {
       width: 18px;
       height: 18px;
   }

   .carousel-track-container {
       width: 100%;
       padding: 0;
       overflow: hidden;
   }

   .carousel-item {
       padding: 15px;
   }
}
</style>
